import * as moment from "moment";
import { ConvertedCostRevenue } from "../../types";
import { EffectiveRentUtil } from "../headline-rent";
import { totalNonRecoverable, totalRecoverable } from "../../utils";
import { basisMultiplier, miniFs, rounddown4 } from "./utils";
import { ModelAssumptionsUtil } from "./model-assumptions-util";

export function calculateLeaseholdDualRateWithASFAndAllowanceForTax(
  costRevenue: ConvertedCostRevenue,
  subCostRevenue: ConvertedCostRevenue,
  valuationDate: Date,
  {
    activeBreakOffOption, 
    allRiskYield, 
    annualSinkingFund, 
    taxRate, 
    timingId
  }: {
    activeBreakOffOption: boolean,
    allRiskYield: number,
    annualSinkingFund: number,
    taxRate: number,
    timingId: number
  }
) {
  const _basisMultiplier = basisMultiplier(costRevenue.rentBasis)
  const _valuationDate = valuationDate ? moment(valuationDate) : null
  const yearsToReview = _valuationDate ? ModelAssumptionsUtil.yearsToReview(costRevenue, _valuationDate) : null
  const yearsToWriteOff = _valuationDate ? ModelAssumptionsUtil.yearsToWriteOff(costRevenue, _valuationDate) : null
  const yearsToBreakOff = _valuationDate ? ModelAssumptionsUtil.yearsToBreakOff(costRevenue, _valuationDate) : null

  const headEffectiveRent = EffectiveRentUtil.getValueFromConvertedCostRevenue(costRevenue)
  const headTotalRecoverable = totalRecoverable(costRevenue) * _basisMultiplier
  const headTotalNonRecoverable = totalNonRecoverable(costRevenue) * _basisMultiplier
  const passingRent = headEffectiveRent + headTotalRecoverable - headTotalNonRecoverable

  const _subBasisMultiplier = subCostRevenue ? basisMultiplier(subCostRevenue.rentBasis) : undefined
  const subEffectiveRent = subCostRevenue ? EffectiveRentUtil.getValueFromConvertedCostRevenue(subCostRevenue) : undefined
  const subTotalRecoverable = subCostRevenue ? totalRecoverable(subCostRevenue) * _subBasisMultiplier : undefined
  const subTotalNonRecoverable = subCostRevenue ? totalNonRecoverable(subCostRevenue) * _subBasisMultiplier : undefined
  const profitRent = subEffectiveRent + subTotalRecoverable - subTotalNonRecoverable - passingRent

  const minYear = miniFs(
    activeBreakOffOption
      ? {'Years to Review': yearsToReview, 'Years to Write off': yearsToWriteOff, 'Years to Break off': yearsToBreakOff}
      : {'Years to Review': yearsToReview, 'Years to Write off': yearsToWriteOff}
  )
  const _yieldPurchase = yieldPurchase(allRiskYield, annualSinkingFund, taxRate, minYear.value, timingId) 
  const capitalValue = profitRent * _yieldPurchase

  return {
    headEffectiveRent,
    basisMultiplier: _basisMultiplier,
    headRecoverable: {
      total: headTotalRecoverable,
      recoverablePropertyManagement: costRevenue.recoverablePropertyManagement * _basisMultiplier,
      recoverableLeasingExpenses: costRevenue.recoverableLeasingExpenses * _basisMultiplier, 
      recoverableUtilities: costRevenue.recoverableUtilities * _basisMultiplier,
      recoverableMaintenance: costRevenue.recoverableMaintenance * _basisMultiplier,
      recoverableInsurance: costRevenue.recoverableInsurance * _basisMultiplier,
      recoverableJanitorial: costRevenue.recoverableJanitorial * _basisMultiplier,
      recoverablePropertyTaxes: costRevenue.recoverablePropertyTaxes * _basisMultiplier,
      recoverableBusiness: costRevenue.recoverableBusiness * _basisMultiplier,
      recoverableOthers: costRevenue.recoverableOthers * _basisMultiplier,
    },
    headNonRecoverable: {
      total: headTotalNonRecoverable,
      nonRecoverablePropertyManagement: costRevenue.nonRecoverablePropertyManagement * _basisMultiplier,
      nonRecoverableLeasingExpenses: costRevenue.nonRecoverableLeasingExpenses * _basisMultiplier, 
      nonRecoverableUtilities: costRevenue.nonRecoverableUtilities * _basisMultiplier,
      nonRecoverableMaintenance: costRevenue.nonRecoverableMaintenance * _basisMultiplier,
      nonRecoverableInsurance: costRevenue.nonRecoverableInsurance * _basisMultiplier,
      nonRecoverableJanitorial: costRevenue.nonRecoverableJanitorial * _basisMultiplier,
      nonRecoverablePropertyTaxes: costRevenue.nonRecoverablePropertyTaxes * _basisMultiplier,
      nonRecoverableBusiness: costRevenue.nonRecoverableBusiness * _basisMultiplier,
      nonRecoverableOthers: costRevenue.nonRecoverableOthers * _basisMultiplier,
    },
    passingRent,
    subEffectiveRent,
    subBasisMultiplier: _subBasisMultiplier,
    subRecoverable: {
      total: subTotalRecoverable,
      recoverablePropertyManagement: subCostRevenue ? subCostRevenue.recoverablePropertyManagement * _subBasisMultiplier : undefined,
      recoverableLeasingExpenses: subCostRevenue ? subCostRevenue.recoverableLeasingExpenses * _subBasisMultiplier : undefined, 
      recoverableUtilities: subCostRevenue ? subCostRevenue.recoverableUtilities * _subBasisMultiplier : undefined,
      recoverableMaintenance: subCostRevenue ? subCostRevenue.recoverableMaintenance * _subBasisMultiplier : undefined,
      recoverableInsurance: subCostRevenue ? subCostRevenue.recoverableInsurance * _subBasisMultiplier : undefined,
      recoverableJanitorial: subCostRevenue ? subCostRevenue.recoverableJanitorial * _subBasisMultiplier : undefined,
      recoverablePropertyTaxes: subCostRevenue ? subCostRevenue.recoverablePropertyTaxes * _subBasisMultiplier : undefined,
      recoverableBusiness: subCostRevenue ? subCostRevenue.recoverableBusiness * _subBasisMultiplier : undefined,
      recoverableOthers: subCostRevenue ? subCostRevenue.recoverableOthers * _subBasisMultiplier : undefined,
    },
    subNonRecoverable: {
      total: subTotalNonRecoverable,
      nonRecoverablePropertyManagement: subCostRevenue ? subCostRevenue.nonRecoverablePropertyManagement * _subBasisMultiplier : undefined,
      nonRecoverableLeasingExpenses: subCostRevenue ? subCostRevenue.nonRecoverableLeasingExpenses * _subBasisMultiplier : undefined, 
      nonRecoverableUtilities: subCostRevenue ? subCostRevenue.nonRecoverableUtilities * _subBasisMultiplier : undefined,
      nonRecoverableMaintenance: subCostRevenue ? subCostRevenue.nonRecoverableMaintenance * _subBasisMultiplier : undefined,
      nonRecoverableInsurance: subCostRevenue ? subCostRevenue.nonRecoverableInsurance * _subBasisMultiplier : undefined,
      nonRecoverableJanitorial: subCostRevenue ? subCostRevenue.nonRecoverableJanitorial * _subBasisMultiplier : undefined,
      nonRecoverablePropertyTaxes: subCostRevenue ? subCostRevenue.nonRecoverablePropertyTaxes * _subBasisMultiplier : undefined,
      nonRecoverableBusiness: subCostRevenue ? subCostRevenue.nonRecoverableBusiness * _subBasisMultiplier : undefined,
      nonRecoverableOthers: subCostRevenue ? subCostRevenue.nonRecoverableOthers * _subBasisMultiplier : undefined,
    },
    profitRent: isNaN(profitRent) ? undefined : profitRent,
    yieldPurchase: {
      value: _yieldPurchase,
      year: minYear 
    },
    value: capitalValue
  }
}

function yieldPurchase(
  allRiskYield: number,
  annualSinkingFund: number,
  taxRate: number,
  year: number,
  timingId: number
) {
  if (year === null) return undefined
  const ary = allRiskYield / 100
  const asf = annualSinkingFund / 100
  const tax = 100/(100 - taxRate)

  const denom = Math.pow(1 + asf, year) - 1

  const aryV = timingId == 1 
    ? ary
    : timingId == 2
      ? 4 * (1 - Math.pow(1 + ary, -0.25))
      : undefined
  const asfV = timingId == 1
    ? asf
    : timingId == 2
      ? 4 * (1 - Math.pow(1 + asf, -0.25))
      : undefined

  const temp1 = asfV / denom 
  const temp2 = temp1 * tax 
  const temp3 = aryV + temp2
  if (temp3 == 0) return undefined
  const value = 1 / temp3
  return rounddown4(value)
}